import { useMemo } from 'react'
import { useGetSignalRConnection } from 'App/hooks'
import {
  type AppShellDataProps,
  type Authentication,
} from '@trr/app-shell-data'

import { mergeEpiBlockContent } from '../../utils'

import { EpiEditorialBlock } from 'state/PublicWebApi/Settings'
import { useCachedUserProfileState } from 'state/PublicWebApi/UserProfile'
import { useSliceStateSelector } from 'slices/hooks'
import { useCachedContentQuery } from 'PublicWebApi'

const useSharedState = (
  currentKey: string,
  editorBlockContent: EpiEditorialBlock
): AppShellDataProps & {
  signalRConnection: {
    klient?: unknown
    medarbetare?: unknown
  }
} => {
  const { profile, idToken, isLoggedIn, state } = useSliceStateSelector(
    (state) => state.slices.auth
  )
  const signalRConnection = useGetSignalRConnection()
  const { data: contentData } = useCachedContentQuery()
  const mergedEpiBlockContent = mergeEpiBlockContent(
    contentData?.properties?.blocks
  )
  const currentPageImages = contentData?.properties?.currentPageImages
  const device = useSliceStateSelector(
    (state) => state.slices.device.deviceSize
  )
  const selectedLanguage = useSliceStateSelector(
    (state) => state.slices.language.selectedLanguage
  )
  const epiPagePath = contentData?.properties?.url
  const userProfile = useCachedUserProfileState()
  const enabledFeatures = window.FEATURE_FLAGS
  const content = useMemo(
    () => ({
      ...mergedEpiBlockContent,
      ...editorBlockContent,
      urlKey: currentKey,
      currentPageImages,
    }),
    [mergedEpiBlockContent, editorBlockContent, currentKey, currentPageImages]
  )

  const authentication: Authentication = useMemo(
    () => ({
      sub: profile.sub ?? null,
      idp: profile.idp,
      idToken: idToken ?? null,
      userRoles: profile.roles ?? [],
      state,
      isLoggedIn: isLoggedIn ?? false,
    }),
    [idToken, profile, state, isLoggedIn]
  )

  return {
    content,
    device,
    enabledFeatures,
    user: userProfile,
    authentication,
    currentKey,
    epiPagePath,
    signalRConnection: signalRConnection ?? {},
    selectedLanguage,
  }
}

export default useSharedState
